import { play, unlockAudio } from '@/audio';
import { musicWanted, startMusic } from '@/audio/music';

/**
 * Global interaction sounds, installed once via event delegation.
 *
 * Doing this centrally rather than per component means every button in the app
 * is audible without touching it, and a new one is audible the moment it
 * exists. Components only call `play()` directly for things that are not a
 * generic click, like a march leaving or a battle report landing.
 */

let installed = false;

/** Elements that should sound like a button when clicked. */
const INTERACTIVE = 'button, a, [role="button"], summary';

function isMuted(el: Element | null): boolean {
  return Boolean(el?.closest('[data-quiet]'));
}

export function installUiSounds(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  /*
   * Browsers refuse to start audio before a real gesture. Unlock on the first
   * one of any kind, then let the click handler below make the actual sound.
   *
   * The score needs the same treatment and was not getting it. Unlocking
   * resumes the Web Audio context, but the music is an <audio> element outside
   * that graph: its play() is attempted once when the settings store loads,
   * long before anybody has clicked, refused by the autoplay policy, and never
   * tried again. The result was a game that silently had no music at all.
   */
  const unlock = (): void => {
    unlockAudio();
    if (musicWanted()) startMusic();
  };
  document.addEventListener('pointerdown', unlock, { capture: true });
  document.addEventListener('keydown', unlock, { capture: true });

  document.addEventListener(
    'click',
    (event) => {
      const target = (event.target as Element | null)?.closest(INTERACTIVE);
      if (!target || isMuted(target)) return;
      if (target instanceof HTMLButtonElement && target.disabled) return;

      // Destructive buttons get the duller sound, so the audio agrees with
      // what the colour is already telling you.
      //
      // The forum is a reading room: its clicks sound at a fifth of the
      // game's volume, matching its hover blips already being off.
      const onForum =
        window.location.hostname.startsWith('forum.') ||
        window.location.pathname.startsWith('/forum');
      play(target.classList.contains('btn--danger') ? 'cancel' : 'click', onForum ? 0.2 : 1);
    },
    { capture: true },
  );

  document.addEventListener(
    'pointerover',
    (event) => {
      if ((event as PointerEvent).pointerType === 'touch') return;
      const target = (event.target as Element | null)?.closest(INTERACTIVE);
      if (!target || isMuted(target)) return;
      // Reading surfaces opt out of hover blips: sweeping a cursor down a
      // list of forum boards is browsing, not pressing, and a tick per row
      // turns a quiet room into a metronome. Clicks still sound.
      if (target.closest('[data-quiet-hover]')) return;
      if (target instanceof HTMLButtonElement && target.disabled) return;
      play('hover');
    },
    { capture: true },
  );

  // Range sliders and checkboxes are not buttons but still deserve a tick.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target as HTMLInputElement | null;
      if (!target || isMuted(target)) return;
      if (target.type === 'checkbox' || target.type === 'radio') play('clickSoft');
    },
    { capture: true },
  );
}
